import { useState } from "react";
import { X, Crosshair, Navigation, Ban, BatteryCharging, Power, Plus, Trash2 } from "lucide-react";

/**
 * Hidden operator console. Unlocked by tapping the TRACKER logo 8 times and
 * entering the passphrase. Dumb component: App owns the API calls and the
 * armed map-click state; this just renders the roster and the controls.
 */
export const AdminConsole = ({ agents, armed, onArm, onHalt, onBattery, onState, onClose, onInspect, traces = [], onDeleteTrace }) => {
  const [selId, setSelId] = useState(null);
  const all = agents || [];
  const sel = all.find((a) => a.id === selId) || null;
  const isGold = sel && sel.id === "agent-goldcoast";

  const pickRow = (a) => {
    const next = a.id === selId ? null : a.id;
    setSelId(next);
    // Also select on the main map (flies the view there) so idle assets —
    // which aren't drawn otherwise — become visible while being commanded.
    if (next && onInspect) onInspect(next);
  };

  return (
    <div className="admin-console" data-testid="admin-console">
      <div className="admin-header">
        <span>⬤ OPERATOR CONSOLE</span>
        <button onClick={onClose} data-testid="admin-close"><X size={12} /></button>
      </div>

      <div className="admin-list">
        {all.map((a) => (
          <button
            key={a.id}
            className={`admin-row ${a.id === selId ? "selected" : ""}`}
            onClick={() => pickRow(a)}
            data-testid={`admin-row-${a.id}`}
          >
            <span className={`admin-code ${a.id === "agent-goldcoast" ? "gold" : ""}`}>{a.code}</span>
            <span className="admin-city" />
            <span className={`admin-status${a.admin_state === "off" ? " off" : a.admin_state === "on" ? " on" : ""}`}>
              {a.admin_state === "off"
                ? "OFF"
                : a.signal_searching ? "LOST" : a.drone ? "AIR" : a.status.toUpperCase()}
            </span>
          </button>
        ))}
      </div>

      {sel && (
        <div className="admin-actions">
          <div className="admin-power-row">
            <span className="admin-power-label"><Power size={10} /> SIGNAL</span>
            <div className="admin-seg">
              {["auto", "on", "off"].map((s) => {
                const cur = (sel.admin_state || "auto");
                return (
                  <button
                    key={s}
                    className={`admin-seg-btn ${cur === s ? "active " + s : ""}`}
                    onClick={() => onState(sel.id, s)}
                    data-testid={`admin-state-${s}`}
                  >
                    {s.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="admin-actions-row">
            <button
              className={`admin-btn ${armed && armed.mode === "move" ? "armed" : ""}`}
              onClick={() => onArm(armed && armed.mode === "move" ? null : { mode: "move", agentId: sel.id })}
              title="Teleport asset to a map point"
              data-testid="admin-move"
            >
              <Crosshair size={11} /> MOVE
            </button>
            <button
              className={`admin-btn ${armed && armed.mode === "dispatch" ? "armed" : ""}`}
              onClick={() => onArm(armed && armed.mode === "dispatch" ? null : { mode: "dispatch", agentId: sel.id })}
              title="Send asset toward a map point"
              data-testid="admin-dispatch"
            >
              <Navigation size={11} /> DISPATCH
            </button>
            <button className="admin-btn" onClick={() => onHalt(sel.id)} title="Cancel current leg" data-testid="admin-halt">
              <Ban size={11} /> HALT
            </button>
          </div>

          {isGold && (
            <div className="admin-battery-row">
              <BatteryCharging size={11} />
              <input
                type="range"
                min="0"
                max="100"
                defaultValue={sel.battery ?? 100}
                onMouseUp={(e) => onBattery(sel.id, Number(e.target.value))}
                onTouchEnd={(e) => onBattery(sel.id, Number(e.target.value))}
                data-testid="admin-battery"
              />
              <span className="admin-batt-label">PWR</span>
            </div>
          )}

          {armed && armed.mode !== "trace-add" && (
            <div className="admin-armed-hint" data-testid="admin-armed-hint">
              CLICK MAP TO {armed.mode === "move" ? "PLACE" : "TARGET"} · ESC CANCELS
            </div>
          )}
        </div>
      )}

      <div className="admin-traces">
        <div className="admin-traces-head">
          <span><Crosshair size={11} /> TRACES · {traces.length}</span>
          <button
            className={`admin-btn ${armed && armed.mode === "trace-add" ? "armed" : ""}`}
            onClick={() => onArm(armed && armed.mode === "trace-add" ? null : { mode: "trace-add" })}
            data-testid="admin-trace-add"
            title="Tap the map to drop traces"
          >
            <Plus size={11} /> ADD
          </button>
        </div>
        {armed && armed.mode === "trace-add" && (
          <div className="admin-armed-hint" data-testid="admin-trace-hint">
            CLICK MAP TO DROP TRACE · ESC STOPS
          </div>
        )}
        <div className="admin-trace-list" data-testid="admin-trace-list">
          {traces.length === 0 && <div className="admin-trace-empty">No live traces.</div>}
          {traces.map((t) => (
            <div key={t.id} className="admin-trace-row">
              <span className="admin-trace-meta">
                {t.poi_name || "Trace"} · {t.region}
              </span>
              <button
                className="admin-trace-del"
                onClick={() => onDeleteTrace && onDeleteTrace(t.id)}
                data-testid={`admin-trace-del-${t.id}`}
                title="Delete trace"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
